import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import {
  Users, Settings, UserPlus, UserMinus,
  FileText, Loader2, Clock as ClockIcon, Share2, Lock
} from 'lucide-react';
import { resolveUrl } from './shared';

const CommunityHeader = ({
  community, isCreator, isMember, joinLoading,
  onlineCount, onEdit, onToggleMembership, isPending, onShare
}) => {
  const bannerStyle = community.banner_url
    ? { backgroundImage: `url(${resolveUrl(community.banner_url)})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)' };
  const joinMode = community.join_mode || 'open';
  const isPrivate = joinMode === 'invitation';
  const locked = !!community.content_locked;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden mb-6" data-testid="community-header">
      <div className="h-44 sm:h-56 relative" style={bannerStyle}>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
      </div>
      <div className="relative px-4 sm:px-6 pb-6">
        <div className="absolute -top-14 left-4 sm:left-6">
          <Avatar className="h-28 w-28 border-4 border-white dark:border-gray-800 shadow-xl">
            <AvatarImage src={resolveUrl(community.avatar_url)} />
            <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-2xl font-bold">{community.name?.substring(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
        </div>
        <div className="flex justify-end pt-4 gap-2 flex-wrap">
          {/* Brief 27 oct B.2 — Partager dans l'en-tête de chaque communauté */}
          {onShare && (
            <Button variant="outline" onClick={onShare} data-testid="community-share-btn" title="Partager cette communauté">
              <Share2 className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Partager</span>
            </Button>
          )}
          {isCreator ? (
            <Button variant="outline" onClick={onEdit} data-testid="community-manage-btn"><Settings className="h-4 w-4 mr-2" />Gérer</Button>
          ) : isPending ? (
            <Button variant="outline" disabled data-testid="community-pending-btn">
              <ClockIcon className="h-4 w-4 mr-2 text-amber-500" /> Demande envoyée
            </Button>
          ) : (
            <Button onClick={onToggleMembership} disabled={joinLoading} variant={isMember ? "outline" : "default"} className={!isMember ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" : ""} data-testid="community-join-btn">
              {joinLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : isMember ? <><UserMinus className="h-4 w-4 mr-2" />Quitter</> : <><UserPlus className="h-4 w-4 mr-2" />{isPrivate ? 'Demander à rejoindre' : 'Rejoindre'}</>}
            </Button>
          )}
        </div>
        <div className="mt-10">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white" data-testid="community-name">{community.name}</h1>
            <span
              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${isPrivate ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}
              data-testid="community-join-mode-badge"
            >
              {isPrivate && <Lock className="h-3 w-3" />}
              {isPrivate ? 'Privée · sur invitation' : 'Ouverte'}
            </span>
          </div>
          {community.category && <span className="inline-block mt-2 px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-full text-sm font-medium">{community.category}</span>}
          <div className="flex items-center gap-6 mt-3 text-sm text-gray-600 dark:text-gray-300 flex-wrap">
            <span className="flex items-center"><Users className="h-4 w-4 mr-1.5 text-purple-500" /><strong className="text-gray-900 dark:text-white mr-1">{Math.max(0, community.member_count || 0)}</strong>{(community.member_count || 0) > 1 ? 'membres' : 'membre'}</span>
            {!locked && (
              <>
                <span className="flex items-center"><span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 animate-pulse"></span><strong className="text-gray-900 dark:text-white mr-1">{onlineCount}</strong>en ligne</span>
                <span className="flex items-center"><FileText className="h-4 w-4 mr-1.5 text-blue-500" /><strong className="text-gray-900 dark:text-white mr-1">{community.post_count || 0}</strong>{(community.post_count || 0) > 1 ? 'publications' : 'publication'}</span>
              </>
            )}
          </div>
          {community.description && <p className="text-gray-600 dark:text-gray-300 mt-3 leading-relaxed" data-testid="community-description">{community.description}</p>}
        </div>
      </div>
    </div>
  );
};

export default CommunityHeader;
